import { NavLink, Outlet, Navigate, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FolderKanban,
  Ticket,
  ScrollText,
  Search,
  Shield,
  Building2,
  Package,
  BookOpen,
  Store,
  Users,
  Globe,
  Settings2,
  ChevronDown,
  ChevronRight,
  Layers,
  Cpu,
  CloudCog,
  ClipboardCheck,
  BadgeCheck,
  Eye,
  HardDrive,
  Boxes,
  LifeBuoy,
  Tags,
} from "lucide-react";
import { useEffect, useMemo, useState, type ElementType } from "react";
import { AgentOtaButton } from "../components/AgentOtaButton";
import { NotificationBell } from "../components/NotificationBell";
import { UserAccountMenu } from "../components/UserAccountMenu";
import { PersonalCenterHost } from "../components/PersonalCenterHost";
import { PhoneBindNudge } from "../components/PhoneBindNudge";
import { SupportHelpFab } from "../components/SupportHelpFab";
import { useAuth } from "../auth/AuthContext";
import { useProjects } from "../data/ProjectsContext";
import { isHostBound } from "../utils/hostBinding";

function isPlatformAdmin(role: string | null): boolean {
  return role === "platform_admin";
}

type NavLeaf = { to: string; label: string; icon: ElementType };

type NavEntry =
  | { kind: "link"; item: NavLeaf }
  | {
      kind: "accordion";
      id: string;
      label: string;
      icon: ElementType;
      children: NavLeaf[];
    };

function BrandMark() {
  return (
    <div className="brand-mark" aria-hidden>
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M4 12 L12 4 L20 12 L12 20 Z" strokeWidth="1.8" />
        <circle cx="12" cy="12" r="2.4" />
      </svg>
    </div>
  );
}

function pathMatches(pathname: string, to: string) {
  if (pathname === to) return true;
  return pathname.startsWith(`${to}/`);
}

function accordionChildActive(pathname: string, children: NavLeaf[]) {
  return children.some((c) => pathMatches(pathname, c.to));
}

export function AppLayout() {
  const { isAuthenticated, role } = useAuth();
  const {
    list: projects,
    cloudReady,
    getById,
    isCloudBacked,
    refreshFromCloud,
  } = useProjects();
  const location = useLocation();
  const [q, setQ] = useState("");
  /** 对齐 ROOMKIT：同时只展开一组；随路由自动展开当前组 */
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  const routeProjectId = useMemo(() => {
    const m = location.pathname.match(/^\/projects\/([^/]+)/);
    return m?.[1] || null;
  }, [location.pathname]);

  const routeProject = useMemo(() => {
    if (!routeProjectId) return undefined;
    return getById(routeProjectId) || projects.find((p) => p.id === routeProjectId);
  }, [routeProjectId, getById, projects]);

  const showTopbarOta =
    Boolean(routeProject) &&
    isHostBound(routeProject!) &&
    isCloudBacked(routeProject!.id);

  const navEntries = useMemo((): NavEntry[] => {
    const platformAdmin = isPlatformAdmin(role);

    if (platformAdmin) {
      return [
        {
          kind: "link",
          item: { to: "/dashboard", label: "平台概览", icon: LayoutDashboard },
        },
        {
          kind: "accordion",
          id: "user-mgmt",
          label: "用户管理",
          icon: Building2,
          children: [
            { to: "/admin/tenants", label: "租户管理", icon: Building2 },
            {
              to: "/admin/support-requests",
              label: "租户求助",
              icon: LifeBuoy,
            },
          ],
        },
        {
          kind: "accordion",
          id: "platform-capability",
          label: "平台能力",
          icon: CloudCog,
          children: [
            { to: "/admin/api-config", label: "API 配置", icon: CloudCog },
            { to: "/admin/ai-factors", label: "因子管理", icon: Layers },
            { to: "/admin/ai-algorithms", label: "算法应用", icon: Cpu },
            { to: "/admin/cognitive", label: "空间认知", icon: Eye },
            {
              to: "/admin/mijia-hub",
              label: "中枢能力",
              icon: Boxes,
            },
            {
              to: "/admin/integration-mirrors",
              label: "集成本库",
              icon: Store,
            },
            {
              to: "/admin/agent",
              label: "OTA 发版",
              icon: Package,
            },
            {
              to: "/resources",
              label: "安装指引",
              icon: BookOpen,
            },
          ],
        },
        {
          kind: "accordion",
          id: "project-ops",
          label: "项目运营",
          icon: FolderKanban,
          children: [
            { to: "/admin/projects", label: "项目调试", icon: FolderKanban },
            {
              to: "/admin/device-category-maps",
              label: "设备品类对照",
              icon: Tags,
            },
            {
              to: "/admin/delivery-standards",
              label: "交付标准",
              icon: ClipboardCheck,
            },
          ],
        },
        {
          kind: "accordion",
          id: "platform-config",
          label: "平台配置",
          icon: Settings2,
          children: [
            { to: "/admin/site-config", label: "站点配置", icon: Globe },
            { to: "/admin/system-users", label: "系统用户", icon: Users },
            { to: "/admin/roles", label: "平台角色", icon: Shield },
            { to: "/admin/audit", label: "平台审计", icon: ScrollText },
          ],
        },
      ];
    }

    return [
      {
        kind: "link",
        item: { to: "/dashboard", label: "工作台", icon: LayoutDashboard },
      },
      {
        kind: "link",
        item: { to: "/projects", label: "项目列表", icon: FolderKanban },
      },
      {
        kind: "link",
        item: { to: "/devices", label: "硬件设备", icon: HardDrive },
      },
      {
        kind: "accordion",
        id: "customer-support",
        label: "客户支持",
        icon: Ticket,
        children: [
          { to: "/tickets", label: "运维工单", icon: Ticket },
          { to: "/resources", label: "安装指引", icon: Package },
        ],
      },
      {
        kind: "accordion",
        id: "system",
        label: "系统管理",
        icon: Settings2,
        children: [
          { to: "/certification", label: "企业认证", icon: BadgeCheck },
          { to: "/users", label: "人员管理", icon: Users },
          { to: "/roles", label: "角色管理", icon: Shield },
          { to: "/audit", label: "操作日志", icon: ScrollText },
        ],
      },
    ];
  }, [role]);

  const searchHits = useMemo(() => {
    const key = q.trim().toLowerCase();
    if (!key) return [];
    return projects
      .filter(
        (p) =>
          p.name.toLowerCase().includes(key) ||
          p.customer.toLowerCase().includes(key) ||
          p.address.toLowerCase().includes(key),
      )
      .slice(0, 6);
  }, [q, projects]);

  const platformAdmin = isPlatformAdmin(role);
  const debugShell = /\/debug(\/|$)/.test(location.pathname);

  useEffect(() => {
    const current = navEntries.find(
      (e) =>
        e.kind === "accordion" &&
        accordionChildActive(location.pathname, e.children),
    );
    if (current && current.kind === "accordion") {
      setExpandedMenus((prev) =>
        prev.includes(current.id) ? prev : [current.id],
      );
    }
  }, [location.pathname, navEntries]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const toggleAccordion = (id: string) => {
    setExpandedMenus((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [id],
    );
  };

  const workspaceTitle = "UDHUB · 云枢";
  const workspaceSubtitle = platformAdmin
    ? "平台运营后台"
    : "集成商运维平台";

  return (
    <div className={`app-shell${debugShell ? " debug-shell" : ""}`}>
      {/* ROOMKIT 式：全宽顶栏 */}
      {!debugShell ? (
        <header className="shell-header">
          <div className="shell-header-brand">
            <BrandMark />
            <div className="shell-header-titles">
              <h1 className="shell-header-title">{workspaceTitle}</h1>
              <span className="shell-header-sep" aria-hidden />
              <p className="shell-header-sub">{workspaceSubtitle}</p>
            </div>
          </div>
          <div className="shell-header-actions">
            {!platformAdmin ? (
              <div className="topbar-search">
                <Search size={16} />
                <input
                  placeholder="搜索项目 / 设备 / 工单"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  aria-label="全局搜索"
                />
                {searchHits.length > 0 ? (
                  <div className="search-popover">
                    {searchHits.map((p) => (
                      <Link
                        key={p.id}
                        to={`/projects/${p.id}`}
                        onClick={() => setQ("")}
                      >
                        <strong>{p.name}</strong>
                        <span>{p.address}</span>
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
            {showTopbarOta && routeProject ? (
              <AgentOtaButton
                projectId={routeProject.id}
                agent={routeProject.agent}
                hostBound
                agentOnline={routeProject.agentStatus === "已连接"}
                cloudReady={cloudReady}
                onUpdated={() => {
                  void refreshFromCloud();
                }}
                className="btn btn-ghost btn-xs"
                placement="hero"
              />
            ) : null}
            <NotificationBell />
            <UserAccountMenu />
          </div>
        </header>
      ) : null}

      <div className="shell-body">
        <aside
          className="sidebar"
          aria-hidden={debugShell ? true : undefined}
          inert={debugShell ? true : undefined}
        >
          <nav className="nav" aria-label="主导航">
            {navEntries.map((entry) => {
              if (entry.kind === "link") {
                const Icon = entry.item.icon;
                return (
                  <NavLink
                    key={entry.item.to}
                    to={entry.item.to}
                    end={entry.item.to === "/dashboard"}
                    className={({ isActive }) =>
                      `nav-link${isActive ? " active" : ""}`
                    }
                  >
                    <span className="nav-link-icon" aria-hidden>
                      <Icon size={18} />
                    </span>
                    <span className="nav-link-label">{entry.item.label}</span>
                  </NavLink>
                );
              }

              const Icon = entry.icon;
              const childActive = accordionChildActive(
                location.pathname,
                entry.children,
              );
              const expanded = expandedMenus.includes(entry.id);

              return (
                <div
                  key={entry.id}
                  className={`nav-accordion${expanded ? " is-open" : ""}${
                    childActive ? " is-active" : ""
                  }`}
                >
                  <button
                    type="button"
                    className={`nav-accordion-trigger${
                      childActive ? " active" : ""
                    }`}
                    aria-expanded={expanded}
                    onClick={() => toggleAccordion(entry.id)}
                  >
                    <span className="nav-link-icon" aria-hidden>
                      <Icon size={18} />
                    </span>
                    <span className="nav-link-label">{entry.label}</span>
                    {expanded ? (
                      <ChevronDown
                        size={16}
                        strokeWidth={2}
                        className="nav-accordion-chevron"
                        aria-hidden
                      />
                    ) : (
                      <ChevronRight
                        size={16}
                        strokeWidth={2}
                        className="nav-accordion-chevron"
                        aria-hidden
                      />
                    )}
                  </button>
                  {expanded ? (
                    <div
                      className="nav-accordion-children"
                      role="group"
                      aria-label={`${entry.label}子菜单`}
                    >
                      {entry.children.map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          className={({ isActive }) =>
                            `nav-child-link${isActive ? " active" : ""}`
                          }
                        >
                          {/* ROOMKIT：子项左侧占位对齐，不重复图标 */}
                          <span className="nav-child-spacer" aria-hidden />
                          <span className="nav-link-label">{child.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </nav>
        </aside>

        <main className="main" data-admin-content>
          <Outlet />
        </main>
      </div>
      <PersonalCenterHost />
      <PhoneBindNudge />
      {!platformAdmin ? <SupportHelpFab /> : null}
    </div>
  );
}

export { BrandMark };
